import { Award, BookOpen, ChevronDown, Edit, FileText, HelpCircle, Lightbulb, MessageSquare, Users } from "lucide-react";
import { useId, useState } from "react";
import { Button, Section, SectionHeading, accent, accentFor, cn } from "../ui";

export const Faq = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const baseId = useId();

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqItems = [
    {
      question: "How do I publish my academic article on the platform?",
      answer: "Publishing is simple! After logging in, navigate to the 'Create' section and select 'New Article'. Our streamlined editor lets you write, format, and add references without needing technical knowledge. You can save drafts, request peer reviews, and publish when ready. Your work will be discoverable by our global academic community.",
      icon: <Edit size={20} aria-hidden="true" />
    },
    {
      question: "What types of courses are available on the platform?",
      answer: "Our platform hosts a diverse range of academic courses from leading institutions worldwide. You'll find everything from undergraduate introductory courses to specialized graduate-level seminars. Topics span all major disciplines including STEM, humanities, social sciences, business, and arts. Many courses offer certificates upon completion, and some may be eligible for academic credit at participating institutions.",
      icon: <BookOpen size={20} aria-hidden="true" />
    },
    {
      question: "How can I access past exam questions and study materials?",
      answer: "Our comprehensive library of past questions and study materials is available to all registered users. Simply navigate to the 'Resources' section and filter by institution, course, or topic. You can bookmark materials for offline access, create custom study sets, and even contribute your own notes and past papers to help fellow students. Premium members enjoy additional features like AI-powered question analysis and personalized study recommendations.",
      icon: <FileText size={20} aria-hidden="true" />
    },
    {
      question: "How do I apply for scholarships through the platform?",
      answer: "The 'Opportunities' section lists all available scholarships with their eligibility criteria and application deadlines. You can filter opportunities by field of study, geographical region, degree level, and funding amount. Our built-in application system allows you to create a profile once and apply to multiple scholarships efficiently. We also provide application guides and direct connections to scholarship providers for any specific queries.",
      icon: <Award size={20} aria-hidden="true" />
    },
    {
      question: "How do the discussion groups work?",
      answer: "Our discussion groups provide spaces for academic collaboration across disciplines. You can join existing groups or create your own based on research interests, courses, or general academic topics. Each group offers threaded discussions, file sharing, polls, and video meeting integration. Moderators help maintain scholarly standards, and you can set notifications to stay updated on conversations that matter to you. Connect with peers, mentors, and experts in your field!",
      icon: <MessageSquare size={20} aria-hidden="true" />
    },
    {
      question: "What security measures protect my academic work?",
      answer: "We implement comprehensive security protocols to protect your intellectual property. All uploads are timestamped and digitally fingerprinted to establish authorship. Our platform uses end-to-end encryption for sensitive data, multi-factor authentication, and regular security audits. Additionally, you control the visibility of your work with options ranging from completely private to open access, with various permission levels in between.",
      icon: <HelpCircle size={20} aria-hidden="true" />
    },
    {
      question: "Can I collaborate with others on research projects?",
      answer: "Absolutely! Our collaboration tools are designed specifically for academic teamwork. You can invite co-authors to work simultaneously on documents, manage version control, assign tasks, and track progress. The platform supports citation management, dataset sharing, and integration with common research tools. For larger projects, you can establish research groups with customizable workflows and communication channels.",
      icon: <Users size={20} aria-hidden="true" />
    },
    {
      question: "Are there any fees to use the academic platform?",
      answer: "We offer a tiered membership model to suit different needs. Our Basic membership is free and provides access to essential features including article reading, participating in discussion groups, and limited access to study materials. Premium memberships unlock additional benefits like unlimited publishing, advanced analytics, priority support, and complete access to all courses and resources. Special discounts are available for students, early-career researchers, and institutions.",
      icon: <Lightbulb size={20} aria-hidden="true" />
    }
  ];

  const handleContactSupport = () => {
    const mail = "mailto: mindalitix@.com";
    window.open(mail, "_blank");
  };

  return (
    <Section tone="alt">
      <SectionHeading
        align="center"
        eyebrow="FAQ"
        eyebrowAccent={accent('orange').rule}
        title={<>Frequently Asked <span className="mark-gold">Questions</span></>}
        description="Everything you need to know about us"
      />

      {/* One hairline stack. Each row is a real disclosure: a button that owns
          its panel, so screen readers and the keyboard get the same behaviour
          the mouse does.

          Each question carries its own accent swatch, derived from the question
          text so it never shifts between renders. Eight identical grey rows were
          impossible to scan; a colour per row gives the eye somewhere to land
          and a landmark to come back to. State is still carried by the chevron
          rotation and `aria-expanded`, never by the colour. */}
      <div className="mx-auto max-w-3xl divide-y divide-border overflow-hidden rounded-lg border border-border bg-background-card">
        {faqItems.map((item, index) => {
          const isOpen = openIndex === index;
          const buttonId = `${baseId}-faq-trigger-${index}`;
          const panelId = `${baseId}-faq-panel-${index}`;
          const palette = accentFor(item.question);

          return (
            <div key={item.question}>
              <h3>
                <button
                  type="button"
                  id={buttonId}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => toggleAccordion(index)}
                  className="flex w-full items-center gap-4 px-5 py-4 text-left transition-colors duration-200 hover:bg-background-alt focus-visible:outline-none focus-visible:bg-background-alt sm:px-6"
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'flex h-11 w-11 shrink-0 items-center justify-center rounded-lg transition-colors duration-200',
                      palette.soft,
                      palette.text,
                    )}
                  >
                    {item.icon}
                  </span>
                  <span className={cn('flex-1 text-body font-medium', isOpen ? palette.text : 'text-text-primary')}>
                    {item.question}
                  </span>
                  <ChevronDown
                    size={18}
                    aria-hidden="true"
                    className={cn(
                      'shrink-0 text-muted-foreground transition-transform duration-200 ease-editorial',
                      isOpen && cn('rotate-180', palette.text),
                    )}
                  />
                </button>
              </h3>

              <div
                id={panelId}
                role="region"
                aria-labelledby={buttonId}
                hidden={!isOpen}
                className={cn('px-5 pb-6 sm:px-6', isOpen && 'animate-fade-up')}
              >
                {/* The answer hangs off a rule in the row's own colour, which
                    ties it to the question it belongs to while it is open. */}
                <div className="flex gap-4 pl-0 sm:pl-1">
                  <span aria-hidden="true" className={cn('w-0.5 shrink-0 rounded-sm', palette.rule)} />
                  <p className="text-body text-text-secondary">{item.answer}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mx-auto mt-12 max-w-3xl overflow-hidden rounded-lg border border-border bg-background-card">
        <span aria-hidden="true" className={cn('block h-1', accent('orange').rule)} />
        <div className="px-6 py-8 text-center">
          <p className="text-body text-text-secondary">Still have questions about us?</p>
          <div className="mt-5">
            <Button onClick={handleContactSupport}>Contact support</Button>
          </div>
        </div>
      </div>
    </Section>
  );
};

export default Faq
